import React, { useState, useEffect } from 'react';
import bugReportsService, { BugReportConfig } from '../services/bugReports.service';
import { useToast } from '../contexts/ToastContext';
import { useImageAttachments } from '../hooks/useImageAttachments';

interface BugReportFormProps {
  onSuccess?: (workItemId: number) => void;
  onCancel?: () => void;
  isOpen?: boolean; // Track when drawer opens to refresh URL
  onTemporaryClose?: () => void; // Temporarily close drawer for screenshot
  onTemporaryReopen?: () => void; // Reopen drawer after screenshot
  onShowScreenshotModal?: (screenshot: string, updateCallback: (newScreenshot: string) => void) => void; // Callback to show screenshot modal outside drawer
  /** Override the page_url field (used by popout windows that need to report the opener's URL) */
  initialPageUrl?: string;
}

const BugReportForm: React.FC<BugReportFormProps> = ({ onSuccess, onCancel, isOpen, onTemporaryClose, onTemporaryReopen, onShowScreenshotModal, initialPageUrl }) => {
  const { showToast } = useToast();
  const [config, setConfig] = useState<BugReportConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [capturingScreenshot, setCapturingScreenshot] = useState(false);
  // Upload / paste / remove / annotate, shared with the feature-request and market-conditions forms.
  const attachments = useImageAttachments({
    active: !!isOpen,
    onPasted: () => showToast({ message: 'Image pasted successfully!', type: 'success', duration: 2000 }),
  });
  useEffect(() => {
    if (attachments.error) { setError(attachments.error); attachments.setError(null); }
  }, [attachments.error, attachments.setError]);

  const resolvedPageUrl = initialPageUrl || window.location.href;

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    steps_to_reproduce: '',
    browser: '',
    page_url: resolvedPageUrl,
  });

  // Capture system and browser information
  const getSystemInfo = () => {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
    const language = navigator.language;
    const screenResolution = `${window.screen.width}x${window.screen.height}`;
    const viewport = `${window.innerWidth}x${window.innerHeight}`;

    return {
      userAgent,
      platform,
      language,
      screenResolution,
      viewport,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timestamp: new Date().toISOString()
    };
  };

  // Capture screenshot of the current page
  const captureScreenshot = async () => {
    setCapturingScreenshot(true);
    try {
      // Close the drawer so it's not in the screenshot
      if (onTemporaryClose) {
        onTemporaryClose();
      }
      await new Promise(resolve => setTimeout(resolve, 150));

      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });

      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      await new Promise<void>(resolve => { video.onloadedmetadata = () => resolve(); });
      await video.play();
      await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d')!.drawImage(video, 0, 0);
      stream.getTracks().forEach(track => track.stop());

      setScreenshot(canvas.toDataURL('image/png'));

      if (onTemporaryReopen) {
        onTemporaryReopen();
      }
    } catch (error: any) {
      console.error('Failed to capture screenshot:', error);
      if (error?.name !== 'NotAllowedError') {
        setError('Failed to capture screenshot. Please try again.');
      }
      if (onTemporaryReopen) {
        onTemporaryReopen();
      }
    } finally {
      setCapturingScreenshot(false);
    }
  };

  // Remove screenshot
  const removeScreenshot = () => {
    setScreenshot(null);
  };

  // Fetch configuration on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const cfg = await bugReportsService.getConfig();
        setConfig(cfg);
      } catch (err) {
        console.error('Failed to load bug report config:', err);
      }
    };
    loadConfig();

    // Capture full user agent string and current page URL
    const userAgent = navigator.userAgent;
    setFormData(prev => ({
      ...prev,
      browser: userAgent,
      page_url: initialPageUrl || window.location.href
    }));
  }, [initialPageUrl]);

  // Capture current page URL when drawer opens
  // Note: This is intentional URL synchronization - captures URL at moment of open.
  // No onOpen callback available, so watching isOpen prop change is the correct pattern.
  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({
        ...prev,
        page_url: initialPageUrl || window.location.href
      }));
    }
  }, [isOpen, initialPageUrl]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const systemInfo = getSystemInfo();

      const response = await bugReportsService.submitBugReport({
        title: formData.title,
        description: formData.description,
        steps_to_reproduce: formData.steps_to_reproduce || undefined,
        browser: formData.browser || undefined,
        page_url: formData.page_url || undefined,
        screenshot_url: screenshot || undefined,
        uploaded_images: attachments.images.length > 0 ? attachments.images : undefined,
        additional_info: {
          system: systemInfo
        }
      });

      // Show success notification
      showToast({
        message: `Ticket #${response.id} submitted successfully!`,
        type: 'success',
        duration: 5000
      });

      // Reset form for next submission
      setFormData({
        title: '',
        description: '',
        steps_to_reproduce: '',
        browser: formData.browser, // Keep browser info
        page_url: initialPageUrl || window.location.href, // Reset to current page (or opener URL in popout)
      });
      setScreenshot(null); // Clear screenshot
      attachments.reset(); // Clear uploaded images

      if (onSuccess) {
        onSuccess(response.id);
      }

      // Close the drawer after successful submission
      if (onCancel) {
        onCancel();
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to submit bug report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (!config) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!config.enabled) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-600 dark:text-gray-400">
          Bug reporting is not currently configured. Please contact your administrator.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-6">
      <div>
        <h3 className="text-lg font-medium text-heading mb-4">
          Report a Bug
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Please provide as much detail as possible to help us resolve the issue quickly.
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-400">
                {error}
              </h3>
            </div>
          </div>
        </div>
      )}

      <div>
        <label htmlFor="title" className="block text-sm font-medium text-label">
          Bug Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="title"
          name="title"
          required
          minLength={5}
          maxLength={255}
          value={formData.title}
          onChange={handleChange}
          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
          placeholder="Brief description of the bug"
        />
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-label">
          Description <span className="text-red-500">*</span>
        </label>
        <textarea
          id="description"
          name="description"
          required
          minLength={10}
          rows={4}
          value={formData.description}
          onChange={handleChange}
          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
          placeholder="What happened? What did you expect to happen?"
        />
      </div>

      <div>
        <label htmlFor="steps_to_reproduce" className="block text-sm font-medium text-label">
          Steps to Reproduce
        </label>
        <textarea
          id="steps_to_reproduce"
          name="steps_to_reproduce"
          rows={3}
          value={formData.steps_to_reproduce}
          onChange={handleChange}
          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
          placeholder="1. Go to...&#10;2. Click on...&#10;3. See error..."
        />
      </div>

      {/* Screenshot Section */}
      <div>
        <label className="block text-sm font-medium text-label mb-2">
          Capture Screenshot (Optional)
        </label>
        {!screenshot ? (
          <button
            type="button"
            onClick={captureScreenshot}
            disabled={capturingScreenshot}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-label bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {capturingScreenshot ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Capturing...
              </>
            ) : (
              <>
                <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Capture Screenshot
              </>
            )}
          </button>
        ) : (
          <div className="relative">
            <img
              src={screenshot}
              alt="Screenshot preview"
              onClick={() => onShowScreenshotModal?.(screenshot, (newScreenshot) => setScreenshot(newScreenshot))}
              className="w-full h-auto rounded-md border border-gray-300 dark:border-gray-600 cursor-pointer hover:opacity-90 transition-opacity"
              title="Click to view and edit"
            />
            <button
              type="button"
              onClick={removeScreenshot}
              className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Upload Images Section */}
      <div>
        <label className="block text-sm font-medium text-label mb-2">
          Upload Images (Optional)
        </label>
        <p className="text-xs text-muted mb-2">
          You can also paste images directly from your clipboard (Ctrl+V / Cmd+V)
        </p>
        <div className="space-y-3">
          <label className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-label bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer">
            <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Upload Images
            <input
              type="file"
              multiple
              accept="image/png,image/jpeg,image/jpg,image/gif"
              onChange={attachments.handleFileInput}
              className="hidden"
            />
          </label>

          {attachments.images.length > 0 && (
            <div className="grid grid-cols-2 gap-3">
              {attachments.images.map((image, index) => (
                <div key={index} className="relative">
                  <img
                    src={image}
                    alt={`Uploaded image ${index + 1}`}
                    onClick={() => onShowScreenshotModal?.(image, (newImage) => attachments.updateImage(index, newImage))}
                    className="w-full h-auto rounded-md border border-gray-300 dark:border-gray-600 cursor-pointer hover:opacity-90 transition-opacity"
                    title="Click to view and edit"
                  />
                  <button
                    type="button"
                    onClick={() => attachments.removeImage(index)}
                    className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="browser" className="block text-sm font-medium text-label">
          Browser
        </label>
        <input
          type="text"
          id="browser"
          name="browser"
          value={formData.browser}
          readOnly
          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm sm:text-sm dark:bg-gray-700 dark:text-white bg-gray-50 dark:bg-gray-800 cursor-not-allowed"
          placeholder="Chrome, Firefox, Safari, etc."
        />
      </div>

      <div>
        <label htmlFor="page_url" className="block text-sm font-medium text-label">
          Page URL
        </label>
        <input
          type="text"
          id="page_url"
          name="page_url"
          value={formData.page_url}
          readOnly
          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm sm:text-sm dark:bg-gray-700 dark:text-white bg-gray-50 dark:bg-gray-800 cursor-not-allowed"
          placeholder="https://..."
        />
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-label bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Submitting...' : 'Submit Bug Report'}
        </button>
      </div>

    </form>
  );
};

export default BugReportForm;
